import { get, sum, sumBy, toNumber } from 'lodash';
import {
  FinalFee,
  FinalFeeType,
  FinalProduct,
  FinanceResponseData,
  Insurance,
  InsuranceType,
  OtherCharge,
} from '@upstart/harmony/lib/legos/financingTypes';
import {
  getTradeInAllowance,
  getTradeInPayoff,
} from '@upstart/harmony/lib/dealer-utils';
import { fmtPrice } from '@upstart/harmony/lib/formatter';
import { SumValueType } from './ricFormTypes';

export function getFinalizedFee(
  finalizedFeeList: FinalFee[] | undefined,
  type: FinalFeeType | undefined,
): FinalFee | undefined {
  return finalizedFeeList?.find((f) => f.type === type);
}

export function getFinalizedFees(
  finalizedFeeList: FinalFee[] | undefined,
  type: FinalFeeType | undefined,
): FinalFee[] {
  return finalizedFeeList?.filter((f) => f.type === type) ?? [];
}

export function getInsurance(
  insurances: Insurance[] | undefined,
  type: InsuranceType | undefined,
): Insurance | undefined {
  return insurances?.find((i) => i.type === type);
}

export function getProductsTotal(products: FinalProduct[]): number {
  return sumBy(products, 'value') || 0;
}

export function getFeesTotal(fees: FinalFee[]): number {
  return sumBy(fees, 'value') || 0;
}

export function getInsurancesTotal(insurances: Insurance[]): number {
  return sumBy(insurances, 'premium') || 0;
}

export function getOtherChargesTotal(otherCharges: OtherCharge[]): number {
  return sumBy(otherCharges, 'amount') || 0;
}

export function parseNumberIfPresent(val: string): number | null {
  return val === '' ? null : toNumber(val);
}

export function formatCurrency(number?: number | string | null): string {
  if (!number && number !== 0) {
    return 'N/A';
  }
  return toNumber(number) < 0 ? `-${fmtPrice(-number)}` : fmtPrice(number);
}

export function calculateTotalDownPayment(
  responseData: FinanceResponseData,
): number {
  const tradeIns = responseData?.tradeIns ?? [];
  const tradeInAllowance = getTradeInAllowance(tradeIns);
  const tradePayoff = getTradeInPayoff(tradeIns);
  const netTrade = tradeInAllowance - tradePayoff;
  return (
    sum([
      responseData?.deferredDownPayment ?? 0,
      sumBy(responseData?.otherDownPayments, 'amount') || 0,
      responseData?.downPayment ?? 0,
      responseData?.rebate ?? 0,
      netTrade,
    ]) || 0
  );
}

export function calculateSumValue(
  responseData: FinanceResponseData,
  sumValue: SumValueType,
): number {
  let total = 0;

  if (sumValue.responseDataFields) {
    total += sumValue.responseDataFields.reduce((a, responseDataField) => {
      const val = get(responseData, responseDataField);
      return a + (get(val, 'value') || get(val, 'amount') || val || 0);
    }, 0);
  }

  if (sumValue.otherCharges) {
    total += sumValue.otherCharges.reduce((a, otherChargeField) => {
      const val = get(responseData, otherChargeField);
      return a + (sumBy(val, 'amount') || 0);
    }, 0);
  }

  if (sumValue.feeTypes) {
    const fees = responseData.finalizedFeeList?.filter((f) =>
      sumValue.feeTypes?.includes(f.type),
    );
    total += sumBy(fees, 'value') || 0;
  }

  if (sumValue.productCategories) {
    const products = responseData.finalizedProductList?.filter((p) =>
      sumValue.productCategories?.includes(p.category ?? undefined),
    );
    total += sumBy(products, 'value') || 0;
  }

  if (sumValue.insuranceTypes) {
    total += sumValue.insuranceTypes.reduce((a, insuranceType) => {
      const insurances = responseData?.insurances?.filter(
        (i) => i.type === insuranceType,
      );
      return a + (sumBy(insurances, 'premium') || 0);
    }, 0);
  }

  if (
    sumValue.downPaymentPayoff ||
    sumValue.downPaymentWithMinZero ||
    sumValue.subtractDownPaymentWithMinZero
  ) {
    const totalDownpayment = calculateTotalDownPayment(responseData);

    if (sumValue.downPaymentPayoff) {
      total += Math.max(-totalDownpayment, 0);
    }
    if (sumValue.subtractDownPaymentWithMinZero) {
      total -= Math.max(totalDownpayment, 0);
    }
    if (sumValue.downPaymentWithMinZero) {
      total += Math.max(totalDownpayment, 0);
    }
  }

  return total;
}
